/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/bluemateVatCountryRow.scss';
import {AwesomeIcon, ElementPosition, ErrorType} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {getCountryLabel} from '../../../util/countryUtil';
import {VATType, VATValue} from '../../../util/interfaces';
import {translateVatValue, VATValuesMatch} from '../../../util/vatUtil';
import CountryFlag from '../../misc/countryFlag';
import BluemateSegmentedToggle from '../button/bluemateSegmentedToggle';
import BluemateInput from '../input/inputController';

interface Props {
    countryCode: string;
    value: VATValue | null;
    options: Array<VATValue>;
    onSelect: (countryCode: string, value: VATValue) => void;
    onChangeCustom: (value: string) => void;
    onDelete?: VoidFunction;
    error?: ErrorType;
    disabled?: boolean;
    children?: ReactNode;
}

const percentOrnament = {element: <span className='percent-ornament'>%</span>, placement: ElementPosition.RIGHT};

export function optionKey(option: VATValue) {
    return `${option.vat_type}-${typeof option.value === 'number' ? option.value : 0}`;
}

export function isCustomVAT(value: VATValue | null, options: Array<VATValue>): boolean {
    if (!value || ![VATType.DEFAULT, VATType.CUSTOM].includes(value.vat_type)) return false;
    if (value.vat_type === VATType.CUSTOM) return true;
    return !options.find((option: VATValue) => VATValuesMatch(value, option));
}

export default function BluemateVatCountryRow(props: Props) {
    const {countryCode, value, options, onSelect, onChangeCustom, onDelete, error, disabled, children} = props;
    const {t} = useTranslation();
    const custom = isCustomVAT(value, options);
    const selectedKey = getSelectedKey();

    function getSelectedKey() {
        if (isCustomVAT(value, options))
            return optionKey(options.find(option => option.vat_type === VATType.CUSTOM) ?? options[0]);
        if (value && [VATType.MARGIN, VATType.SPLIT].includes(value.vat_type)) {
            const byType = options.find(option => option.vat_type === value.vat_type);
            if (byType) return optionKey(byType);
        }
        const match = options.find(option => VATValuesMatch(value, option));
        return match ? optionKey(match) : '';
    }

    function onChangeToggle(key: string) {
        if (key === selectedKey) return;
        const option = options.find(entry => optionKey(entry) === key);
        if (option) onSelect(countryCode, option);
    }

    function toggleOption(option: VATValue) {
        return {value: optionKey(option), label: translateVatValue(option)};
    }

    return (
        <div className='bluemate-vat-country-row'>
            {!!error && <label className='error-label'>{error}</label>}
            <CountryFlag countryCode={countryCode} />
            <span className={classNames('country-label', !!error && 'error')}>{getCountryLabel(countryCode)}</span>
            <div className={classNames('vat-toggle', disabled && 'disabled')}>
                <BluemateSegmentedToggle
                    options={options.map(toggleOption)}
                    value={selectedKey}
                    onChange={onChangeToggle}
                />
            </div>
            {!!onDelete && (
                <AwesomeIcon name='trash-alt' onClick={disabled ? undefined : onDelete} className='btn-delete' />
            )}
            {custom && (
                <BluemateInput
                    onChange={onChangeCustom}
                    value={typeof value?.value === 'number' ? value.value : ''}
                    label={t('settings.vat.customRate', 'Aangepast tarief')}
                    ornaments={percentOrnament}
                    disabled={disabled}
                    allowCharReg={/[0-9]/}
                    inputProps={{inputMode: 'numeric', maxLength: 3}}
                    className='custom-vat-field'
                    shape='rounded'
                    uniformFont
                    autoFocus
                    autoSelect
                />
            )}
            {children}
        </div>
    );
}
